import React from 'react';
import { AppTheme } from '../types';

interface ChartProps {
  theme: AppTheme;
  type: 'bar' | 'line' | 'pie' | 'doughnut';
  data: ChartData[];
  title: string;
  width?: string;
  height?: string;
}

interface ChartData {
  label: string;
  value: number;
  color?: string;
}

export const Chart: React.FC<ChartProps> = ({ 
  theme, 
  type, 
  data, 
  title, 
  width = '400px', 
  height = '300px' 
}) => {
  const containerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    padding: '16px',
    width,
    height,
    display: 'flex',
    flexDirection: 'column',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 'normal',
    color: theme.colors.text,
    marginBottom: '16px',
    textAlign: 'center',
  };

  const chartAreaStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: '8px',
    paddingBottom: '20px',
    position: 'relative',
  };

  const maxValue = Math.max(...data.map(d => d.value));

  const renderBarChart = () => {
    return (
      <div style={chartAreaStyle}>
        {data.map((item, index) => {
          const barHeight = (item.value / maxValue) * 200;
          const barStyle: React.CSSProperties = {
            width: '40px',
            height: `${barHeight}px`,
            backgroundColor: item.color || theme.colors.primary,
            borderRadius: '2px 2px 0 0',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
            position: 'relative',
          };

          const labelStyle: React.CSSProperties = {
            fontSize: '10px',
            color: theme.colors.text,
            marginTop: '4px',
            textAlign: 'center',
            maxWidth: '50px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          };

          const valueStyle: React.CSSProperties = {
            fontSize: '9px',
            color: 'white',
            position: 'absolute',
            top: '-15px',
            fontWeight: 'bold',
          };

          return (
            <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={barStyle}>
                <span style={valueStyle}>{item.value}</span>
              </div>
              <div style={labelStyle}>{item.label}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderLineChart = () => {
    const chartStyle: React.CSSProperties = {
      ...chartAreaStyle,
      position: 'relative',
      alignItems: 'center',
    };

    const svgStyle: React.CSSProperties = {
      width: '100%',
      height: '200px',
    };

    const points = data.map((item, index) => {
      const x = (index / (data.length - 1)) * 300 + 50;
      const y = 180 - (item.value / maxValue) * 150;
      return { x, y, value: item.value, label: item.label };
    });

    const pathData = points.map((point, index) => 
      index === 0 ? `M ${point.x} ${point.y}` : `L ${point.x} ${point.y}`
    ).join(' ');

    return (
      <div style={chartStyle}>
        <svg style={svgStyle}>
          <path
            d={pathData}
            fill="none"
            stroke={theme.colors.primary}
            strokeWidth="2"
          />
          {points.map((point, index) => (
            <g key={index}>
              <circle
                cx={point.x}
                cy={point.y}
                r="4"
                fill={theme.colors.primary}
              />
              <text
                x={point.x}
                y={point.y - 10}
                textAnchor="middle"
                fontSize="10"
                fill={theme.colors.text}
              >
                {point.value}
              </text>
              <text
                x={point.x}
                y={195}
                textAnchor="middle"
                fontSize="9"
                fill={theme.colors.text}
              >
                {point.label}
              </text>
            </g>
          ))}
        </svg>
      </div>
    );
  };

  const renderPieChart = () => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    let currentAngle = 0;
    const radius = 80;
    const centerX = 120;
    const centerY = 100;

    const colors = ['#003497', '#4d7fff', '#80a3ff', '#b3ccff', '#e6f0ff'];

    return (
      <div style={{...chartAreaStyle, justifyContent: 'space-around', alignItems: 'center'}}>
        <svg width="240" height="200">
          {data.map((item, index) => {
            const angle = (item.value / total) * 360;
            const startAngle = currentAngle;
            const endAngle = currentAngle + angle;
            
            const x1 = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
            const y1 = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
            const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
            const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);
            
            const largeArcFlag = angle > 180 ? 1 : 0;
            
            const pathData = [
              `M ${centerX} ${centerY}`,
              `L ${x1} ${y1}`,
              `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
              'Z'
            ].join(' ');

            currentAngle += angle;

            return (
              <path
                key={index}
                d={pathData}
                fill={item.color || colors[index % colors.length]}
                stroke={theme.colors.background}
                strokeWidth="1"
              />
            );
          })}
        </svg>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {data.map((item, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: item.color || colors[index % colors.length],
                  borderRadius: '2px',
                }}
              />
              <span style={{ fontSize: '11px', color: theme.colors.text }}>
                {item.label}: {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return renderBarChart();
      case 'line':
        return renderLineChart();
      case 'pie':
      case 'doughnut':
        return renderPieChart();
      default:
        return renderBarChart();
    }
  };

  return (
    <div style={containerStyle}>
      <h4 style={titleStyle}>{title}</h4>
      {renderChart()}
    </div>
  );
};